const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class LaboratoryOrderService {
  static async getAllOrders(token, limit = 100, lastKey = null, options = {}) {
    let url = `${API_BASE_URL}/laboratory-orders?limit=${limit}`;
    if (lastKey) {
      url += `&lastKey=${encodeURIComponent(JSON.stringify(lastKey))}`;
    }
    if (options.patientId) {
      url += `&patientId=${encodeURIComponent(options.patientId)}`;
    }
    if (options.providerId) {
      url += `&providerId=${encodeURIComponent(options.providerId)}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      orders: data.data || [],
      pagination: data.pagination || {}
    };
  }

  static async getAllOrdersAllPages(token, limit = 100, options = {}) {
    let allOrders = [];
    let lastKey = null;

    do {
      const result = await LaboratoryOrderService.getAllOrders(token, limit, lastKey, options);
      allOrders = allOrders.concat(result.orders || []);
      lastKey = result.pagination?.lastKey || result.pagination?.lastEvaluatedKey || null;
    } while (lastKey);

    return allOrders;
  }

  static async getOrdersByPatientAllPages(patientId, token, limit = 100) {
    return LaboratoryOrderService.getAllOrdersAllPages(token, limit, { patientId });
  }

  static async getOrdersByProviderAllPages(providerId, token, limit = 100) {
    return LaboratoryOrderService.getAllOrdersAllPages(token, limit, { providerId });
  }

  static async createOrder(orderData, token) {
    const response = await fetch(`${API_BASE_URL}/laboratory-orders`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(orderData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  }

  static async getOrderById(orderId, token) {
    const response = await fetch(`${API_BASE_URL}/laboratory-orders/${orderId}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  }

  static async updateOrder(orderId, orderData, token) {
    const response = await fetch(`${API_BASE_URL}/laboratory-orders/${orderId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(orderData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  }

  static async updateOrderStatus(orderId, payload, token) {
    const response = await fetch(`${API_BASE_URL}/laboratory-orders/${orderId}/status`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  }

  static async getAttachmentUploadUrl(orderId, fileData, token) {
    const response = await fetch(`${API_BASE_URL}/laboratory-orders/${orderId}/attachments/upload-url`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(fileData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  }

  static async uploadAttachmentFile(uploadUrl, file, contentType) {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType
      },
      body: file
    });

    if (!response.ok) {
      throw new Error('No se pudo subir el archivo adjunto');
    }
  }

  static async attachFile(orderId, payload, token) {
    const response = await fetch(`${API_BASE_URL}/laboratory-orders/${orderId}/attachments`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  }

  static async deleteAttachment(orderId, attachmentId, token) {
    const response = await fetch(`${API_BASE_URL}/laboratory-orders/${orderId}/attachments/${attachmentId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  }

  static async addPayment(orderId, payment, token) {
    const response = await fetch(`${API_BASE_URL}/laboratory-orders/${orderId}/payments`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payment)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  }

  static async updatePayment(orderId, paymentId, payment, token) {
    const response = await fetch(`${API_BASE_URL}/laboratory-orders/${orderId}/payments/${paymentId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payment)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  }

  static async deletePayment(orderId, paymentId, token) {
    const response = await fetch(`${API_BASE_URL}/laboratory-orders/${orderId}/payments/${paymentId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  }
}

export default LaboratoryOrderService;